import React, { useState, useEffect, useMemo } from 'react';
import { X, Save, Target, Plus, Trash2, Zap } from 'lucide-react';
import { ActionImpact } from '../types/postit';
import { useProjectStore } from '../store/useProjectStore';

interface KaizenImpactModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: KaizenImpactData) => void;
  initialWhat?: string;
  linkedNodeId?: string | null;
  linkedPostItId?: string | null;
  initialImpactos?: ActionImpact[];
}

export interface KaizenImpactData {
  what: string;
  impactos: ActionImpact[];
  linkedNodeId?: string | null;
  linkedPostItId?: string | null;
}

export const KaizenImpactModal: React.FC<KaizenImpactModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialWhat = '',
  linkedNodeId,
  linkedPostItId,
  initialImpactos,
}) => {
  const sheets = useProjectStore((state) => state.sheets);
  const activeSheetId = useProjectStore((state) => state.activeSheetId);
  
  const activeCards = useMemo(() => {
    if (!activeSheetId || sheets.length === 0) return [];
    const activeSheet = sheets.find(s => s.id === activeSheetId);
    return activeSheet?.cards || [];
  }, [sheets, activeSheetId]);

  const [what, setWhat] = useState(initialWhat);
  const [impactos, setImpactos] = useState<ActionImpact[]>([]);

  useEffect(() => {
    if (isOpen) {
      setWhat(initialWhat);
      setImpactos(initialImpactos || []);
    }
  }, [isOpen, initialWhat, initialImpactos]);

  const handleAddImpacto = () => {
    const newImpacto: ActionImpact = {
      targetNodeId: '',
      tipo: 'TRA',
      reducao: 20,
    };
    setImpactos([...impactos, newImpacto]);
  };

  const handleRemoveImpacto = (index: number) => {
    setImpactos(impactos.filter((_, i) => i !== index));
  };

  const handleUpdateImpacto = (index: number, field: keyof ActionImpact, value: ActionImpact[keyof ActionImpact]) => {
    setImpactos(current => {
      const updated = [...current];
      const next = { ...updated[index], [field]: value };
      if (field === 'tipo' && value === 'ELIMINAR') next.reducao = 100;
      updated[index] = next;
      return updated;
    });
  };

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!what.trim()) {
      alert('Descreva a melhoria');
      return;
    }

    onSave({
      what: what.trim(),
      impactos,
      linkedNodeId,
      linkedPostItId,
    });
  };

  // Obter nome do card vinculado
  const linkedCard = activeCards.find(c => c.id === linkedNodeId);
  const linkedCardName = linkedCard?.atividade || linkedCard?.nome_fluxo || 'Card';

  return (
    <div className="fixed right-0 bottom-[24px] z-[200] flex justify-end pointer-events-none" style={{ top: 'calc(var(--shell-header-height, 42px) + var(--shell-ribbon-height, 52px))' }}>
      {/* Painel lateral direito - mesmo estilo do KaizenLogPanel */}
      <div className="w-80 h-full bg-white dark:bg-[var(--bg-surface)] flex flex-col shadow-xl border-l border-slate-200 dark:border-slate-700 overflow-hidden pointer-events-auto">
        {/* Header */}
        <div className="h-12 px-3 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between bg-slate-50 dark:bg-slate-800 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-green-600 rounded flex items-center justify-center">
              <Zap size={12} className="text-white" />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                Impacto da Melhoria
              </div>
              <div className="text-[10px] text-slate-500 dark:text-slate-400">
                Defina os efeitos nos processos
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
          >
            <X size={16} />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {/* Card vinculado */}
          {linkedNodeId && (
            <div className="text-[10px] text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 px-2 py-1.5 rounded border border-slate-200 dark:border-slate-700">
              <span className="font-semibold">Vinculado a:</span> {linkedCardName}
            </div>
          )}

          {/* Descrição da melhoria */}
          <div>
            <label className="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
              Descrição da Melhoria
            </label>
            <textarea
              value={what}
              onChange={(e) => setWhat(e.target.value)}
              className="w-full px-2.5 py-2 border border-slate-300 dark:border-slate-600 rounded text-[11px] bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              rows={2}
              placeholder="O que será melhorado..."
              required
            />
          </div>

          {/* Impactos nos Processos */}
          <div className="bg-green-50 dark:bg-green-900/20 rounded p-2.5 border border-green-200 dark:border-green-800">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5">
                <Target className="text-green-600 dark:text-green-400" size={12} />
                <span className="text-[11px] font-semibold text-slate-800 dark:text-slate-200">Processos Impactados</span>
              </div>
              <button
                type="button"
                onClick={handleAddImpacto}
                className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/40 hover:bg-green-200 dark:hover:bg-green-900/60 rounded transition-colors"
              >
                <Plus size={10} />
                Adicionar
              </button>
            </div>
            
            <div className="space-y-2 max-h-[180px] overflow-y-auto">
              {impactos.length === 0 ? (
                <div className="text-center py-3 text-slate-500 dark:text-slate-400 text-[10px]">
                  Clique em "Adicionar" para definir os processos impactados
                </div>
              ) : (
                impactos.map((impacto, index) => (
                  <div key={index} className="bg-white dark:bg-slate-800 rounded p-2 border border-slate-200 dark:border-slate-600">
                    {/* Processo */}
                    <div className="mb-1.5">
                      <label className="block text-[9px] font-semibold text-slate-500 dark:text-slate-400 mb-0.5">
                        Processo
                      </label>
                      <select
                        value={impacto.targetNodeId}
                        onChange={(e) => handleUpdateImpacto(index, 'targetNodeId', e.target.value)}
                        className="w-full px-1.5 py-1 text-[10px] border border-slate-300 dark:border-slate-600 rounded focus:ring-1 focus:ring-green-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                      >
                        <option value="">Selecione...</option>
                        {activeCards.filter(c => c.tipo === 'atividade' || c.tipo === 'decisao').map(card => (
                          <option key={card.id} value={card.id}>
                            {card.atividade || card.decisao || `Card ${card.id}`}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex gap-2 items-end">
                      {/* Efeito */}
                      <div className="flex-1">
                        <label className="block text-[9px] font-semibold text-slate-500 dark:text-slate-400 mb-0.5">
                          Efeito
                        </label>
                        <select
                          value={impacto.tipo}
                          onChange={(e) => handleUpdateImpacto(index, 'tipo', e.target.value)}
                          className="w-full px-1.5 py-1 text-[10px] border border-slate-300 dark:border-slate-600 rounded focus:ring-1 focus:ring-green-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        >
                          <option value="TRA">Alterar TRA</option>
                          <option value="TP">Alterar TP</option>
                          <option value="ELIMINAR">Eliminar</option>
                        </select>
                      </div>

                      {/* Ganho */}
                      <div className="w-16">
                        <label className="block text-[9px] font-semibold text-slate-500 dark:text-slate-400 mb-0.5">
                          Ganho %
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={impacto.reducao}
                          onChange={(e) => handleUpdateImpacto(index, 'reducao', Number(e.target.value))}
                          disabled={impacto.tipo === 'ELIMINAR'}
                          className="w-full px-1.5 py-1 text-[10px] border border-slate-300 dark:border-slate-600 rounded focus:ring-1 focus:ring-green-500 disabled:bg-slate-100 dark:disabled:bg-slate-700 disabled:cursor-not-allowed bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        />
                      </div>

                      {/* Remover */}
                      <button
                        type="button"
                        onClick={() => handleRemoveImpacto(index)}
                        className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
                        title="Remover"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Dica */}
          <div className="text-[9px] text-slate-400 dark:text-slate-500 bg-slate-50 dark:bg-slate-800 px-2 py-1.5 rounded">
            Estes impactos serão utilizados para simular o estado futuro no mapa
          </div>
        </div>

        {/* Footer */}
        <div className="px-3 py-2.5 border-t border-slate-200 dark:border-slate-700 flex items-center justify-end gap-2 bg-slate-50 dark:bg-slate-800 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-[11px] font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={!what.trim()}
            className="px-3 py-1.5 text-[11px] font-medium text-white bg-green-600 rounded hover:bg-green-700 flex items-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={12} />
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
};

export default KaizenImpactModal;
